import { useState, useEffect, useRef, useMemo } from 'react';

interface UseFileUploadSyncProps {
  files: any[] | undefined;
  uploadQueueFiles: any[];
  allowContinuousUpload: boolean;
  uploadQueue?: any;
  onChangeFiles?: (files: any[]) => void;
}

/**
 * Syncs listFileUpload state with backend files and queue files.
 * Adapted for borrower-dashboard where locally selected files have a
 * data-URL set on .url (via FileReader), while backend files have .key.
 */
export const useFileUploadSync = ({
  files,
  uploadQueueFiles,
  allowContinuousUpload,
  uploadQueue,
  onChangeFiles
}: UseFileUploadSyncProps) => {
  const [listFileUpload, setListFileUpload] = useState<any[]>([]);
  const isInternalUpdate = useRef(false);
  const prevFilesRef = useRef<any[]>([]);

  // Sync with the `files` prop from the parent
  useEffect(() => {
    const filesChanged =
      files !== prevFilesRef.current &&
      (files?.length !== prevFilesRef.current?.length ||
        files?.some((f: any, i: number) => f !== prevFilesRef.current[i]));

    if (!filesChanged && prevFilesRef.current.length > 0) {
      return;
    }

    prevFilesRef.current = files || [];
    isInternalUpdate.current = true;

    if (files === undefined) {
      setListFileUpload([]);
    } else if (files && files.length > 0) {
      // Backend files: identified by .key
      const saved = files.filter((item: any) => item.key);
      const tempFilesFromProp = files.filter((item: any) => item.isTemporary);
      // New files: no key, not temporary (may have local data URL from FileReader)
      const newFileObjects = files.filter((item: any) => !item.key && !item.isTemporary);

      if (allowContinuousUpload) {
        if (tempFilesFromProp.length > 0) {
          setListFileUpload([...saved, ...tempFilesFromProp]);
        } else if (newFileObjects.length > 0) {
          setListFileUpload(prev => {
            const existingTempFiles = prev.filter(
              (item: any) => item.isTemporary && !item.key
            );
            return [...saved, ...existingTempFiles, ...newFileObjects];
          });
        } else {
          // After upload refresh — remove completed temp files
          setListFileUpload(prev => {
            if (saved.length > 0) {
              const tempStillUploading = prev.filter((item: any) => {
                if (!item.isTemporary) return false;
                if (item.queueId) {
                  const queueFile = uploadQueueFiles.find((qf: any) => qf.id === item.queueId);
                  return queueFile && queueFile.status !== 'success';
                }
                return false;
              });
              return [...saved, ...tempStillUploading];
            }
            return prev;
          });
        }
      } else {
        setListFileUpload(saved);
      }
    } else if (files && files.length === 0) {
      setListFileUpload(prev => {
        const filesInQueue = prev.filter((item: any) => {
          if (!item.isTemporary) return false;
          if (item.queueId) {
            return uploadQueueFiles.some((qf: any) => qf.id === item.queueId);
          }
          return false;
        });
        return filesInQueue;
      });
    }

    setTimeout(() => {
      isInternalUpdate.current = false;
    }, 100);
  }, [files, allowContinuousUpload, uploadQueueFiles.length]);

  // Notify parent of user-initiated changes (not internal syncs)
  useEffect(() => {
    if (onChangeFiles && !isInternalUpdate.current) {
      onChangeFiles(listFileUpload);
    }
  }, [listFileUpload, onChangeFiles]);

  // Clear successful queue files once backend files are reflected
  useEffect(() => {
    if (!uploadQueue?.clearSuccessfulFiles) return;
    if (uploadQueue.isProcessing) return;

    const hasDbFiles = listFileUpload.some((f: any) => f.key);
    const hasSuccessInQueue = uploadQueueFiles.some((qf: any) => qf.status === 'success');

    if (hasDbFiles && hasSuccessInQueue) {
      const timer = setTimeout(() => {
        uploadQueue.clearSuccessfulFiles();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [listFileUpload, uploadQueueFiles, uploadQueue]);

  // Enrich list files with queue upload status
  const enrichedFiles = useMemo(() => {
    return listFileUpload.map((file: any) => {
      // Backend file — pass through
      if (file.key) return file;

      // Temp queue file — enrich with status
      if (file.isTemporary && file.queueId) {
        const queueFile = uploadQueueFiles.find((qf: any) => qf.id === file.queueId);
        if (queueFile) {
          return {
            ...file,
            uploadStatus: queueFile.status,
            errorMessage: queueFile.errorMessage,
            onRetry: queueFile.onRetry
          };
        }
      }

      // Local file (not yet in queue) — pass through
      return file;
    });
  }, [listFileUpload, uploadQueueFiles]);

  return {
    enrichedFiles,
    listFileUpload,
    setListFileUpload
  };
};
